import StoryCard from './StoryCard.js';

import {Box, Typography, Button} from '@mui/material';
import '@emotion/react';
import '@emotion/styled';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import {timelineOppositeContentClasses} from '@mui/lab/TimelineOppositeContent';

const timeLineContent = [
    {
        "time":"July 2026",
        "title":"Graduation",
        "content":"Graduated from the University of Bristol with a 2.1 (Second class, first division) BSc Computer Science degree. Majors - Artificial Intelligence, Image Processing & Computer Vision; minors - Security Behaviours, Computational Neuroscience.",
        "id":"7fb40a4a-f855-4b81-af9e-de4ce5f67fa4"
    },
    {
        "time":"January - May 2026",
        "title":"Dissertation",
        "content":<>
            <p>Bachelor's degree dissertation. I trained a Siamese neural network with a ResNet backbone to find user’s location indoors relying only on their visual environment. I used Pytorch, Numpy, OpenCV and Java.</p>
            <p>
                In this project, I explore whether using machine learning is feasible for visual indoor location finding.
This is useful in conditions where satellite or other radio-based navigation is challenging if not impossible.
Walls and ceilings of most buildings reflect satellite signals (e.g. GPS, GLONASS, BeiDou etc.), which
prevents systems located inside from using trilateration. Even under open air visibility, GPS still fails
to achieve accuracy better than a few meters. Visual location finding with machine learning was already
explored in research papers in the past, but researchers were mostly focused on applying it in outdoor
environments.
            </p>
            <p>
                I implemented visual location finding using machine learning in indoor environments.
                I used the Coming Down To Earth (CD2E) model, which Obed Noe Samano Abonce’s work
proved to be efficient in outdoor conditions. CD2E model establishes an embedded space with panoramic
images and raster map tiles in Polar form. This approach outperforms ”You Are Here” approach and
can be applied indoors, as long as building plan is available for feature vector calculation
            </p>
            <p>
                By utilising a ”brute-force” multi-input localisation, I reduced the mean distance error (distance
between predicted node and ground truth node) by 66% comparing to single-input. In real scale terms,
that’s approximately from 18.8 metres to 6.5 metres.
            </p>
            <p>
                Age of the training dataset has a long-term effect on later localisations. In 8 out of 13 test cases,
spherical images that were taken in the same month as the training dataset have smaller feature vector
distances than spherical images that are 3 months newer.
            </p>
            <p>
                I wrote helper software to help collect datasets in an efficient way: Camera Helper, Waypoint
Mapper and Polar Converter. It can be used by other researchers in the future for an open-source
dataset creation, as there is currently no appropriate indoor free-to-use dataset available online.
            </p>
            <ul>
                <li>I used the JavaFX application development platform to develop helper software used for dataset collection.</li>
                <li>I used OpenCV computer vision library to manipulate images, convert map tiles from Cartesian to polar forms, plot heat maps and create multi-input localisation visualisation videos.</li>
                <li>I used PyTorch machine learning framework to train, test and work with neural networks.</li>
            </ul>
            <Button
                variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/IndPIndoorLocator"
                target="_blank"
                role={undefined}
                tabIndex={-1}>GitHub - Main PyTorch code</Button><br/>
            <Button
                variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/IndPWaypointMapper"
                target="_blank"
                role={undefined}
                tabIndex={-1}>GitHub - JavaFX Waypoint Mapper code</Button><br/>
            <Button variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/IndPCameraHelper"
                target="_blank"
                role={undefined}
                tabIndex={-1}>GitHub - JavaFX Camera Helper code</Button><br/>
        </>,
        "id":"fc8256c1-2061-4d7e-8e7a-a06e6dd179f4",
        "skills":["python","numpy","matplotlib","java","opencv","pytorch"]
    },
    {
        "time":"January - May 2026",
        "title":"RISC processor simulator",
        "content":<>
            <p>
                "Advanced Computer Architecture" mini-project. RISC RV32i-like processor simulator in Java, with some assembly programs included as benchmarks
                (including Greatest Common Divisor calculation, Recursive factorial calculation, bubble sort, vector addition with ALU and special vector processing unit operations).

                The simulator has a CLI and supports many assembly instructions from the "Free & Open RISC-V reference card.</p>
            <p>
                It contains 2 register filesets: 32 General Purpose Registers (GPRs) - 1-word size each; 32 Vector Operation Registers (VORs) - 8-word size each. 1 word = 4 bytes.</p>
                <p>Standard environment used in my benchmarks: 2 Arithmetic Logic Units (ALUs), 1 Branch Processing Unit (BPU), 1 Memory Addressing Unit (MAU), 1 Vector Processing Unit 
                    (VPU), 2-bit dynamic branch predictor. EUs can be added or removed by changing the Environment class. 32 general-purpose registers, each of 1 word (32 bit) size and 
                    32 Vector operation registers, each 8-word size.</p>
                <p>My processor supports:</p>
                <ul>
                    <li>Dynamic 1-bit and 2-bit branch predictors.</li>
                    <li>Non-blocking issue (with distributed Reservation Stations)</li>
                    <li>Reorder buffer</li>
                    <li>Register renaming</li>
                    <li>Special execution unit for vector operations</li>
                </ul>

            <Button
                variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/rv32i-processor-simulator"
                target="_blank"
                role={undefined}
                tabIndex={-1}>See the project repository</Button>
        </>,
        "id":"60238210-4e1d-4f13-8784-fe70abaecbd9",
        "skills":["java", "parallelcomp"]
    },
    {
        "time":"January - May 2026",
        "title": "Teaching Assistant at the University of Bristol",
        "content":
            <>
                <p>"Assisted professors with preparing lab sessions, helped to resolve program issues.
                    The unit explained security and privacy of networks and operating systems.
                    Students have been exposed to fundamental security primitives specific to computer systems
                    and networks and to apply them to a broad range of current and future cybersecurity challenges.
                    I assisted students studying software such as Cisco Packet Tracer, Wireshark, VirtualBox and exploring compilers,
                    os161 simulator, Linux source code and some C programming.</p>
                <p>
                    I was also responsible for migrating the week 7 lab code from legacy environment-dependent software to Bash scripts
                    that installed os161 and all required dependencies on any Linux environment, including laptops owned by students if required.
                </p>
                <Button
                variant="text"
                component="a"
                href="https://github.com/cs-uob/COMS20012/blob/master/docs/README.md"
                target="_blank"
                role={undefined}
                tabIndex={-1}>See the unit's repository</Button>
            </>,
        "id":"563eadb5-d587-4fcf-9f73-4563f43d5f8d",
        "skills":["teaching", "linux", "c"]
    },
    {
        "time":"June 2025",
        "title":"Teaching Internship",
        "content":"I have completed my internship at Odyssey Teaching School Hub. I assisted GCSE, BTEC, and A-Level Computing teachers during lessons; helped students to maintain productivity in the classroom; provided extracurricular guidance to high-achieving students; resolved technical issues with Windows OS, Python, and Adobe software; supported and supervised SEND students. This experience enhanced my resilience, communication skills and adaptability, teaching me how to maintain a supportive learning environment even in demanding situations.",
        "id":"60a44b2c-1eae-4371-a62e-ea544e8f11f4",
        "skills":["teaching", "windows", "adobe", "python"]
    },
    {
        "time":"2024-2025",
        "title":"AI Learning Tool",
        "content":<>
            <p>
                In this university group project, we collaborated with a real-world client, John McNamara from IBM. Our task was to create an AI-powered 
            chatbot web app designed to help users find courses on IBM SkillsBuild platform that would suit them the best and answer questions about 
            university life frequently faced by first year students. This project significantly strengthened my skills in full-stack development (Vue 
            JS 3 + Java + Spring Boot), CI/CD (using Docker and GitHub Actions), networking, cloud services and team-based Agile workflows.</p>
            <Button
                variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/2024-AILearningTool"
                target="_blank"
                role={undefined}
                tabIndex={-1}>See the project repository</Button>
            </>,
        "id":"c1cb5aba-cd0b-4051-905c-fb01a29f9efd",
        "skills":["java", "springboot", "vuejs", "html", "js", "mysql", "maven", "jest", "git", "github", "githubwork", "docker", "aws", "linux", "agile"]
    },
    {
        "time":"September - November 2024",
        "title":"Game of Life",
        "content":<>
            University group coursework - implemented parallel and distributed implementation of Conway’s “Game of
            Life” in Go programming language. Distributed edition’s nodes were hosted on AWS EC2 instances.
            <Button
                variant="text"
                component="a"
                href="https://github.com/kirilovich-vlad/gol-in-go"
                target="_blank"
                role={undefined}
                tabIndex={-1}>See the project repository</Button>
            </>,
        "id":"1edc2fab-7b6c-4427-842c-abcbfbec5f6e",
        "skills":["golang", "parallelcomp", "distcomp", "linux", "aws"]
    },
    {
        "time":"July - August 2024",
        "title":"Internship at SHS Estate",
        "content":"In-house Python software development for analysis of the local housing market and more efficient property management. Used Matplotlib, Numpy and Django libraries.",
        "id":"d9453373-ef3e-498f-934a-ab60181697b2",
        "skills":["python", "django", "numpy", "matplotlib"]
    },
    {
        "time":"June 2024 - ongoing",
        "title":"Part/Full-time job at Connolly & Callaghan",
        "content":
        <>
            <p>Initially starting as a temporary office assistant, the managers trusted me to develop on some in-house software projects.</p>
            <p>I have developed a document management software solution based on Microsoft Power Platform that simplifies categorisation of compliance documentation held for different properties.
                It saves time that was spent on manual checking of expiration dates and emails maintenance department employees whenever legal property reassessments are required. Sometimes I also help with IT
                support, such as Windows PC maintenance.</p>
            <p>I also communicate with city councils, business partners over phone and email whenever my supervisor is out of office.</p>
        </>,
        "id":"0fe35a08-7a15-4917-8030-1aad920924f3",
        "skills":["mspp", "windows"]
    },
    {
        "time":"Winter-Spring of 2024",
        "title":"University Java coursework (2024)",
        "content":"As part of a pair-programming coursework, me and my teammate implemented Java-based backend for a 2D implementation of the \"Scotland Yard\" board game.  This project not only deepened my understanding of the Java language but also introduced me to key OOP principles and version control using Git.",
        "id":"c1617f19-f882-4e05-9e56-30df63440e2e",
        "skills":["java", "git", "github"]
    },
    {
        "time":"August 2023",
        "title":"Accepted to university",
        "content":"In August 2023, I've been accepted to the University of Bristol to study Computer Science.",
        "id":"b1d1f61d-58b0-4c5b-927d-f05454a8cf92"
    },
    {
        "time":"2022-2023",
        "title":"Sixth form Python project / MyTourMaker",
        "content":"Between 2021 and 2023, I studied A-Level Computer Science in Sixth Form. Our coursework provided freedom in terms of topic selection, whilst teachers gave us no guidance on how to develop software projects. I used this opportunity to learn Python, PyQt5 framework + other Python libraries, JavaScript (including Leaflet and Mapbox libraries). I also gained experience of integrating different APIs in my project such as Mapbox, OpenStreetMap, OpenRouteService.",
        "id":"24b69a15-8198-4ab0-a8aa-db591ab708a2",
        "skills":["python", "js", "html"]
    }
]

export default function MyStory() {
    let timeLineCards = timeLineContent.map((timeLineEvent) => {
        return({
            "src": timeLineEvent,
            "card": <StoryCard
                    time={timeLineEvent["time"]}
                    title={timeLineEvent["title"]}
                    content={timeLineEvent["content"]}
                    skills={timeLineEvent["skills"]}/>
        })
    })
    
    let timeLineItems = timeLineCards.map((timeLineCard) => {
        return(
            <TimelineItem key={timeLineCard["src"]["id"]}>
                <TimelineOppositeContent color="textSecondary">
                    {timeLineCard["src"]["time"]}
                </TimelineOppositeContent>
                <TimelineSeparator>
                    <TimelineDot sx={{ bgcolor: 'primary.main'}}/>
                    <TimelineConnector/>
                </TimelineSeparator>
                <TimelineContent>{timeLineCard["card"]}</TimelineContent>
            </TimelineItem>
        )
    })

    return (
        <Box item sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            my:4
        }}
        >
            <Typography variant='h3' sx={{fontWeight:800}}>My story</Typography>
            <Timeline
                sx={{
                    [`& .${timelineOppositeContentClasses.root}`]: {
                        flex: 0.3
                    },
                }}
            >
                {timeLineItems}
            </Timeline>
        </Box>
    );
}